import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { MessageInitShape } from '@bufbuild/protobuf'
import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  GenerationSourceTypeForSetting,
  MonitoringUnit,
  OrderBookColumnOrder,
  type PersonalSettings,
  PersonalSettingsSchema,
  type StackOrderItem,
  StackOrderItemSchema,
  Theme,
} from '@ebalance/proto/bffuser/v1/setting_model_pb'
import { getPersonalSettings } from '@ebalance/proto/bffuser/v1/setting_service-SettingService_connectquery'
import {
  GetPersonalSettingsRequestSchema,
  type GetPersonalSettingsResponse,
  GetPersonalSettingsResponseSchema,
  UpdatePersonalSettingsRequestSchema,
} from '@ebalance/proto/bffuser/v1/setting_service_pb'
import { useQueryClient } from '@tanstack/react-query'

import { DEFAULT_STACK_ORDER_ITEMS } from '~/constants/generationSource'
import { useErrorToast } from '~/hooks/useErrorToast'
import { AREA_CODE_OPTIONS } from '~/models/area'
import { FIELD_PATHS } from '~/pages/Settings/constants/defaults'
import {
  beginPersonalSettingsSave,
  endPersonalSettingsSave,
  enqueuePersonalSettingsSave,
  hasOtherPersonalSettingsSave,
  recordPersistedPersonalSettings,
  useIsPersonalSettingsSaving,
} from '~/pages/Settings/hooks/personalSettingsSaveState'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { useSaveSuccessToast } from '~/pages/Settings/hooks/useSaveSuccessToast'
import { useUpdatePersonalSettingsMutation } from '~/pages/Settings/hooks/useUpdatePersonalSettingsMutation'

/** proto Message プロパティを除いた純粋なフィールド値の部分型 */
type PersonalSettingsFields = MessageInitShape<typeof PersonalSettingsSchema>

/**
 * 板の売買列表示順の正規化。 Get が UNSPECIFIED を返す版ずれ (新 frontend + 旧 backend) では
 * 売り左 (既定) として扱い、 保存 payload に 0 を載せない (proto は UNSPECIFIED を拒否する)。
 */
function normalizeOrderBookColumnOrder(
  value: OrderBookColumnOrder | undefined,
): OrderBookColumnOrder {
  return value === OrderBookColumnOrder.BUY_LEFT
    ? OrderBookColumnOrder.BUY_LEFT
    : OrderBookColumnOrder.SELL_LEFT
}

/**
 * 現在の設定値と部分上書き値をマージして MessageInit 型を返す。
 * proto Message 型を直接スプレッドすると $typeName 等の内部プロパティが
 * MessageInit に混入して型エラーになるため、フィールドを明示的に取り出す。
 *
 * PersonalSettings は strict protovalidate rules (enum.not_in: [0],
 * stack_order.min_items = 9) のため、 全フィールドを populate して送る。
 * 全項目置換なので、 `current` は取得済みの設定に限る (未取得の間は `applyOptimistic` / `mutate` の
 * 入口で止める)。 既定値で埋めて送ると、 業務エリア・積み上げ順を既定値で上書きする。
 * 監視単位は廃止した設定 (#3185) で画面に出さないが、 field が wire に残る間は
 * not_in: [0] を満たすため kWh を固定で入れる (Phase 2 で proto から消す)。
 * TODO(#556): true partial payload への移行 (backend usecase + proto + frontend hook の三層同時)
 */
function mergeSettings(
  current: PersonalSettings,
  partial: PersonalSettingsPartial,
): PersonalSettingsFields {
  const currentStackOrder =
    current.stackOrder.length > 0
      ? current.stackOrder
      : DEFAULT_STACK_ORDER_ITEMS

  return {
    theme: partial.theme ?? current.theme,
    monitoringUnit: MonitoringUnit.KWH,
    orderBookColumnOrder: normalizeOrderBookColumnOrder(
      partial.orderBookColumnOrder ?? current.orderBookColumnOrder,
    ),
    showTicker: partial.showTicker ?? current.showTicker,
    businessAreas: partial.businessAreas ?? [...current.businessAreas],
    stackOrder: partial.stackOrder ?? [...currentStackOrder],
    updatedAt: current.updatedAt,
  }
}

type PersonalSettingsPartial = Partial<PersonalSettingsFields>

type PendingSettingsUpdate = {
  settings: PersonalSettings
  paths: Set<string>
}

function rebasePendingSettings(
  pending: PendingSettingsUpdate,
  persisted: PersonalSettings | undefined,
): PersonalSettings {
  if (persisted === undefined) return pending.settings
  const partial: PersonalSettingsPartial = {}
  if (pending.paths.has(FIELD_PATHS.theme))
    partial.theme = pending.settings.theme
  if (pending.paths.has(FIELD_PATHS.orderBookColumnOrder)) {
    partial.orderBookColumnOrder = pending.settings.orderBookColumnOrder
  }
  if (pending.paths.has(FIELD_PATHS.showTicker)) {
    partial.showTicker = pending.settings.showTicker
  }
  if (pending.paths.has(FIELD_PATHS.businessAreas)) {
    partial.businessAreas = [...pending.settings.businessAreas]
  }
  if (pending.paths.has(FIELD_PATHS.stackOrder)) {
    partial.stackOrder = [...pending.settings.stackOrder]
  }
  return create(PersonalSettingsSchema, mergeSettings(persisted, partial))
}

/** `usePersonalSettings` の呼び出し側 option。 既定は設定画面と同じ挙動。 */
export type UsePersonalSettingsOptions = {
  /** `false` の間は取得しない (未指定なら取得する)。 */
  enabled?: boolean
  /**
   * `false` で取得失敗を error boundary へ投げない (既定は QueryClient に従い投げる)。
   * 設定の障害で画面全体を止めたくない画面 (時間前入札の板発注タブ・発電の積み上げ順) 向け。
   */
  throwOnError?: boolean
  /** 保存失敗時に error toast をこのタイトルで出す (設定画面は inline Alert で足りるため未指定)。 */
  saveErrorToastTitle?: string
  /** `false` で保存成功の「保存しました」 toast を出さない (既定 true)。 */
  notifySaveSuccess?: boolean
  /** 保存失敗時 (rollback 後) に呼ぶ。 */
  onSaveError?: () => void
}

export type UsePersonalSettingsReturn = {
  settings: PersonalSettings | undefined
  /**
   * 板の売買列表示順。 読み出し口で 1 回だけ正規化済み (未取得・UNSPECIFIED は売り左)。
   * 呼び出し側は UNSPECIFIED を考慮しない。
   */
  orderBookColumnOrder: OrderBookColumnOrder
  isLoading: boolean
  /** 初回取得が決着していない間 true (成功でも失敗でもなく未決着)。 */
  isPending: boolean
  error: ConnectError | null
  updateTheme: (value: Theme) => void
  updateShowTicker: (value: boolean) => void
  updateOrderBookColumnOrder: (value: OrderBookColumnOrder) => void
  updateBusinessAreas: (areas: AreaCode[]) => void
  selectAllBusinessAreas: () => void
  moveStackOrderUp: (index: number) => void
  moveStackOrderDown: (index: number) => void
  resetStackOrder: () => void
  updateStackOrderColor: (
    source: GenerationSourceTypeForSetting,
    color: string,
  ) => void
  saveError: string | null
}

/**
 * 個人設定の Get + Update を束ねる統合フック。
 *
 * 自動保存ロジック:
 * - ラジオ変更 / チェックボックス変更 / switch 変更は即時 mutation
 * - 積み上げ順の上下ボタンは debounce 500ms、color picker は 300ms
 * - どちらも同じ最新 stack_order と timer を使い、異なる編集の保存が互いを上書きしない
 *
 * 保存の直列化と optimistic update:
 * - 同一タブでは Update を 1 件ずつ送り、保存中の編集は最新の全項目 payload と mask の和集合へ畳み込む
 * - 成功時は応答の updated_at を次の expected_updated_at とし、保存 toast を予約する
 * - 失敗時は最後に保存済みの設定へ rollback し、待機中の編集を破棄して再取得する
 */
export function usePersonalSettings(
  options: UsePersonalSettingsOptions = {},
): UsePersonalSettingsReturn {
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)
  const { data, isLoading, isPending, error } = useGetPersonalSettingsQuery({
    enabled: options.enabled,
    throwOnError: options.throwOnError,
    refetchOnWindowFocus: !isSaving,
  })
  const notifySaveSuccess = options.notifySaveSuccess ?? true
  const onSaveError = options.onSaveError
  const mutation = useUpdatePersonalSettingsMutation()
  const [saveError, setSaveError] = useState<string | null>(null)
  // 保存 toast の通知口 (debounce 450ms)。 保存経路は下の `mutate` 1 箇所なので、
  // 各設定 component ではなくここから通知する。
  const notifySaved = useSaveSuccessToast()
  const showErrorToast = useErrorToast()
  const persistedSettingsRef = useRef<PersonalSettings | undefined>(undefined)
  const inFlightRef = useRef(false)
  const pendingUpdateRef = useRef<PendingSettingsUpdate | null>(null)
  const savingTokenRef = useRef(Symbol('personal-settings-save'))
  const runNextRef = useRef<() => void>(() => undefined)

  // 並べ替え・色変更で共有する debounce timer と最新の stack_order。
  // PersonalSettings.stack_order は全件置換なので、別 timer / payload に分けると
  // 一方の後続保存が他方の編集内容を上書きする。
  const stackDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingStackOrderRef = useRef<StackOrderItem[] | null>(null)
  // debounce 中の最初の編集前の値。保存失敗時の rollback に使う。
  const pendingStackOrderRollbackRef = useRef<StackOrderItem[] | null>(null)

  // query key (optimistic update / invalidate に使う)。
  //
  // **`useGetPersonalSettingsQuery` (= `useQuery`) が購読する key と完全一致させる**。
  // connect-query の `useQuery` は key に `input` と `transport` を含める
  // (`{ serviceName, methodName, transport, cardinality, input: {} }`)。 以前は
  // `{ schema, cardinality }` だけで作っており、 これは invalidate (部分一致) には効くが
  // **`setQueryData` / `getQueryData` (完全一致) では別の key** になっていた。 その結果、
  //   - 楽観更新が誰も購読していない entry に書かれ、 画面は debounce (500ms) → 保存 →
  //     再取得まで変わらなかった (積み上げ順の 🔼 の反応が遅い、 の原因)
  //   - 現在値の読み出し (`getCurrentStackOrderItems`) が常に外れて既定値を返し、
  //     連打しても移動が積み重ならず、 カスタムした並びを既定寄りに戻していた
  // 並び替えの楽観更新を持つ `useUpdateWorkflowTemplateDisplayOrder` と同じ作り方。
  //
  // useMemo で pin する (createConnectQueryKey は毎回新しい array を返すので、
  // pin しないと後段の useCallback chain が毎レンダー再生成される)。
  const transport = useTransport()
  const queryKey = useMemo(
    () =>
      createConnectQueryKey({
        schema: getPersonalSettings,
        cardinality: 'finite',
        input: create(GetPersonalSettingsRequestSchema, {}),
        transport,
      }),
    [transport],
  )

  const protectOptimisticState = useCallback(() => {
    setIsSaving(true)
    beginPersonalSettingsSave(queryClient, savingTokenRef.current)
    // 保存前の Get が後から完了して optimistic cache を巻き戻さないようにする。
    void queryClient.cancelQueries({ queryKey })
  }, [queryClient, queryKey])

  // optimistic update helper: 現在の GetPersonalSettings キャッシュを部分更新
  const applyOptimistic = useCallback(
    (partial: PersonalSettingsPartial) => {
      queryClient.setQueryData<GetPersonalSettingsResponse | undefined>(
        queryKey,
        (old) => {
          // 未取得 (取得中・取得失敗) の間は cache を作らない。 既定値で組んだ settings を書くと、
          // mutate の「取得済みの時だけ送る」 判定がそれを取得済みとみなし、 次の操作で
          // 業務エリア・積み上げ順・監視単位を既定値で上書きする全項目置換を送ってしまう
          const current = old?.settings
          if (current === undefined) return old
          const merged = create(
            PersonalSettingsSchema,
            mergeSettings(current, partial),
          )
          return create(GetPersonalSettingsResponseSchema, {
            settings: merged,
          })
        },
      )
    },
    [queryClient, queryKey],
  )

  // 別の画面の hook が保存している間の cache は楽観更新中の値なので、 保存済みの値として取り込まない
  // (取り込むと、 先行保存が失敗した時にこの hook の rollback が失敗した値を画面へ戻す)。
  // 共有の保存状態が変わったら取り込み直す。
  const isAnyPersonalSettingsSaving = useIsPersonalSettingsSaving()
  useEffect(() => {
    if (
      !isSaving &&
      !inFlightRef.current &&
      pendingUpdateRef.current === null &&
      !hasOtherPersonalSettingsSave(queryClient, savingTokenRef.current)
    ) {
      persistedSettingsRef.current = data?.settings
      recordPersistedPersonalSettings(queryClient, data?.settings)
    }
  }, [data?.settings, isSaving, isAnyPersonalSettingsSaving, queryClient])

  const runNext = useCallback(() => {
    if (inFlightRef.current || pendingUpdateRef.current === null) return

    const next = pendingUpdateRef.current
    pendingUpdateRef.current = null
    inFlightRef.current = true
    // 送信時の保存済み値。 別画面の保存中に開いた hook は自分の ref を持たないので、 失敗時はここへ戻す。
    let sentBaseline: PersonalSettings | undefined
    void enqueuePersonalSettingsSave(
      queryClient,
      (sharedPersisted) => {
        const baseline = sharedPersisted ?? persistedSettingsRef.current
        sentBaseline = baseline
        const settings = rebasePendingSettings(next, baseline)
        return mutation.mutateAsync(
          create(UpdatePersonalSettingsRequestSchema, {
            settings,
            updateMask: { paths: [...next.paths] },
            expectedUpdatedAt: baseline?.updatedAt,
          }),
        )
      },
      (response) => response.settings,
    )
      .then((response) => {
        if (response.settings !== undefined) {
          persistedSettingsRef.current = response.settings
          // 送信中に次の編集が入っている場合、その optimistic 表示を成功
          // response で巻き戻さない。次の送信 baseline だけを更新する。
          if (
            pendingUpdateRef.current === null &&
            pendingStackOrderRef.current === null &&
            !hasOtherPersonalSettingsSave(queryClient, savingTokenRef.current)
          ) {
            queryClient.setQueryData(
              queryKey,
              create(GetPersonalSettingsResponseSchema, {
                settings: response.settings,
              }),
            )
          }
        }
        if (notifySaveSuccess) notifySaved()
      })
      .catch((caught: unknown) => {
        const connectError = caught as ConnectError
        // 失敗時は送信待ちの編集も破棄し、最後に永続化できた全設定へ戻す。
        pendingUpdateRef.current = null
        if (stackDebounceRef.current !== null) {
          clearTimeout(stackDebounceRef.current)
          stackDebounceRef.current = null
        }
        pendingStackOrderRef.current = null
        pendingStackOrderRollbackRef.current = null
        const persisted = sentBaseline ?? persistedSettingsRef.current
        queryClient.setQueryData(
          queryKey,
          persisted === undefined
            ? undefined
            : create(GetPersonalSettingsResponseSchema, {
                settings: persisted,
              }),
        )
        if (
          connectError.code === Code.InvalidArgument &&
          connectError.rawMessage === '[SPS-021]'
        ) {
          setSaveError(
            'page:settings.personal.businessAreas.minimumOneRequired',
          )
        } else {
          setSaveError('page:settings.personal.saveError')
        }
        if (options.saveErrorToastTitle !== undefined) {
          showErrorToast({ title: options.saveErrorToastTitle })
        }
        onSaveError?.()
        void queryClient.invalidateQueries({ queryKey })
      })
      .finally(() => {
        inFlightRef.current = false
        if (
          pendingUpdateRef.current === null &&
          pendingStackOrderRef.current === null
        ) {
          setIsSaving(false)
          endPersonalSettingsSave(queryClient, savingTokenRef.current)
        }
        runNextRef.current()
      })
  }, [
    mutation,
    notifySaveSuccess,
    notifySaved,
    onSaveError,
    options.saveErrorToastTitle,
    queryClient,
    queryKey,
    showErrorToast,
  ])

  useEffect(() => {
    runNextRef.current = runNext
  }, [runNext])

  const mutate = useCallback(
    (
      partial: PersonalSettingsPartial,
      paths: string[],
      _rollbackStackOrder?: StackOrderItem[],
    ) => {
      // 全項目置換のため、 取得済みの設定が無いまま送ると業務エリア・積み上げ順・監視単位を
      // 既定値で上書きする。 未取得 (取得中・取得失敗) の間は何も送らない。
      const currentSettings = queryClient.getQueryData<
        GetPersonalSettingsResponse | undefined
      >(queryKey)?.settings
      if (currentSettings === undefined) return
      protectOptimisticState()
      setSaveError(null)
      const settings = create(
        PersonalSettingsSchema,
        mergeSettings(currentSettings, partial),
      )
      applyOptimistic(partial)

      const pending = pendingUpdateRef.current
      pendingUpdateRef.current = {
        settings,
        paths: new Set([...(pending?.paths ?? []), ...paths]),
      }
      runNextRef.current()
    },
    [applyOptimistic, protectOptimisticState, queryClient, queryKey],
  )

  const updateTheme = useCallback(
    (value: Theme) => {
      // theme は localStorage cache (= first-paint flash 回避) にも書き込む
      // SoT は backend (= mount 後に backend 取得値で再描画)
      const themeKey =
        value === Theme.SYSTEM
          ? 'system'
          : value === Theme.LIGHT
            ? 'light'
            : 'dark'
      try {
        localStorage.setItem('ebalance-theme', themeKey)
      } catch (_) {
        // localStorage 不可環境では silent ignore
      }
      mutate({ theme: value }, [FIELD_PATHS.theme])
    },
    [mutate],
  )

  const updateOrderBookColumnOrder = useCallback(
    (value: OrderBookColumnOrder) => {
      mutate({ orderBookColumnOrder: value }, [
        FIELD_PATHS.orderBookColumnOrder,
      ])
    },
    [mutate],
  )

  const updateShowTicker = useCallback(
    (value: boolean) => {
      mutate({ showTicker: value }, [FIELD_PATHS.showTicker])
    },
    [mutate],
  )

  const updateBusinessAreas = useCallback(
    (areas: AreaCode[]) => {
      // 業務エリアは最低 1 件必須。空配列は UI ロックで発生しないが、
      // 万一渡された場合は無視してサーバー側 0 件保存を防ぐ（AC-SPS-021）。
      if (areas.length === 0) return
      mutate({ businessAreas: areas }, [FIELD_PATHS.businessAreas])
    },
    [mutate],
  )

  const selectAllBusinessAreas = useCallback(() => {
    mutate({ businessAreas: AREA_CODE_OPTIONS }, [FIELD_PATHS.businessAreas])
  }, [mutate])

  // 現在の stackOrder を取得するヘルパー (= 連打対応のため queryClient cache から
  // 同期的に最新値を読む)。
  //
  // 旧実装は `data?.settings?.stackOrder` を見ていたため、 ▲/▼ や color picker
  // の連打時に React 再 render を待たずに 2 回目が走ると stale な `data` を
  // 元に swap してしまい、 design HTML (= 同期的に prefs.StackOrder を直接変異)
  // と挙動が乖離していた。 applyOptimistic が `queryClient.setQueryData` で cache
  // を同期更新しているので、 queryClient から直接読めば常に最新の表示順を返せる。
  const getCurrentStackOrderItems = useCallback((): StackOrderItem[] => {
    const cached = queryClient.getQueryData<
      GetPersonalSettingsResponse | undefined
    >(queryKey)
    const current = cached?.settings?.stackOrder ?? []
    if (current.length > 0) return [...current]
    return DEFAULT_STACK_ORDER_ITEMS.map((item) =>
      create(StackOrderItemSchema, { source: item.source, color: item.color }),
    )
  }, [queryClient, queryKey])

  // 並び替えは 500ms、色変更は 300ms の debounce。save failure では最初の編集前に戻す。
  const flushStackOrder = useCallback(
    (order: StackOrderItem[], rollbackOrder: StackOrderItem[]) => {
      mutate({ stackOrder: order }, [FIELD_PATHS.stackOrder], rollbackOrder)
    },
    [mutate],
  )

  const scheduleStackOrderMutation = useCallback(
    (
      newOrder: StackOrderItem[],
      rollbackOrder: StackOrderItem[],
      debounceMs = 500,
    ) => {
      const currentSettings = queryClient.getQueryData<
        GetPersonalSettingsResponse | undefined
      >(queryKey)?.settings
      if (currentSettings === undefined) return false

      protectOptimisticState()
      if (pendingStackOrderRef.current === null) {
        pendingStackOrderRollbackRef.current = rollbackOrder
      }
      pendingStackOrderRef.current = newOrder
      if (stackDebounceRef.current !== null) {
        clearTimeout(stackDebounceRef.current)
      }
      stackDebounceRef.current = setTimeout(() => {
        if (pendingStackOrderRef.current !== null) {
          flushStackOrder(
            pendingStackOrderRef.current,
            pendingStackOrderRollbackRef.current ??
              pendingStackOrderRef.current,
          )
          pendingStackOrderRef.current = null
          pendingStackOrderRollbackRef.current = null
        }
      }, debounceMs)
      return true
    },
    [flushStackOrder, protectOptimisticState, queryClient, queryKey],
  )

  const moveStackOrderUp = useCallback(
    (index: number) => {
      const currentOrder = getCurrentStackOrderItems()
      if (index <= 0 || index >= currentOrder.length) return
      const newOrder = [...currentOrder]
      ;[newOrder[index - 1], newOrder[index]] = [
        newOrder[index],
        newOrder[index - 1],
      ]
      if (!scheduleStackOrderMutation(newOrder, currentOrder)) return
      applyOptimistic({ stackOrder: newOrder })
    },
    [getCurrentStackOrderItems, applyOptimistic, scheduleStackOrderMutation],
  )

  const moveStackOrderDown = useCallback(
    (index: number) => {
      const currentOrder = getCurrentStackOrderItems()
      if (index < 0 || index >= currentOrder.length - 1) return
      const newOrder = [...currentOrder]
      ;[newOrder[index], newOrder[index + 1]] = [
        newOrder[index + 1],
        newOrder[index],
      ]
      if (!scheduleStackOrderMutation(newOrder, currentOrder)) return
      applyOptimistic({ stackOrder: newOrder })
    },
    [getCurrentStackOrderItems, applyOptimistic, scheduleStackOrderMutation],
  )

  const resetStackOrder = useCallback(() => {
    /* 保留中の debounce を**破棄してから**既定値を書く。
       ↑/↓ を押した直後 (500ms 以内) に「リセット」 を押すと、 リセットは即時
       mutation される一方で先行した並べ替えの debounce が後から flush され、
       サーバーの設定とチャートがリセット前の順序に戻ってしまう
       (色変更後の debounce も同じ timer を使う)。 リセットは「今の状態を捨てて既定に戻す」
       操作なので、 保留中の更新は破棄するのが正しい。
       PR #9163 の bot レビュー指摘。 */
    const rollbackOrder =
      pendingStackOrderRollbackRef.current ?? getCurrentStackOrderItems()
    if (stackDebounceRef.current !== null) {
      clearTimeout(stackDebounceRef.current)
      stackDebounceRef.current = null
    }
    pendingStackOrderRef.current = null
    pendingStackOrderRollbackRef.current = null
    mutate(
      { stackOrder: DEFAULT_STACK_ORDER_ITEMS },
      [FIELD_PATHS.stackOrder],
      rollbackOrder,
    )
  }, [getCurrentStackOrderItems, mutate])

  const updateStackOrderColor = useCallback(
    (source: GenerationSourceTypeForSetting, color: string) => {
      const currentOrder = getCurrentStackOrderItems()
      const newOrder = currentOrder.map((item) =>
        item.source === source
          ? create(StackOrderItemSchema, { source: item.source, color })
          : create(StackOrderItemSchema, {
              source: item.source,
              color: item.color,
            }),
      )
      // color picker は 300ms。並び替えと同じ pending value / timer を使うので、
      // 直前の別編集も最新の全 stack_order に含めて1回の更新で保存する。
      if (!scheduleStackOrderMutation(newOrder, currentOrder, 300)) return
      applyOptimistic({ stackOrder: newOrder })
    },
    [getCurrentStackOrderItems, applyOptimistic, scheduleStackOrderMutation],
  )

  // flushStackOrder の最新版を ref に保持 (= unmount cleanup から呼ぶ際の
  // stale closure 回避。 cleanup の deps に flushStackOrder を入れると毎 render で
  // identity が変わり、 cleanup → 即 flush 連鎖で debounce が壊れるため、
  // deps は [] のまま ref 経由で latest を参照する)
  const flushStackOrderRef = useRef(flushStackOrder)
  useEffect(() => {
    flushStackOrderRef.current = flushStackOrder
  }, [flushStackOrder])

  // unmount 時に debounce timer をクリア + pending を flush
  // (= 500ms 以内に画面遷移すると optimistic 反映済みだが mutation 未発火で
  // 再訪時に invalidate fetch で消える silent data loss を防ぐ)
  useEffect(() => {
    return () => {
      if (stackDebounceRef.current !== null) {
        clearTimeout(stackDebounceRef.current)
      }
      if (pendingStackOrderRef.current !== null) {
        flushStackOrderRef.current(
          pendingStackOrderRef.current,
          pendingStackOrderRollbackRef.current ?? pendingStackOrderRef.current,
        )
        pendingStackOrderRef.current = null
        pendingStackOrderRollbackRef.current = null
      }
    }
  }, [])

  return {
    settings: data?.settings,
    orderBookColumnOrder: normalizeOrderBookColumnOrder(
      data?.settings?.orderBookColumnOrder,
    ),
    isLoading,
    isPending,
    error,
    updateTheme,
    updateShowTicker,
    updateOrderBookColumnOrder,
    updateBusinessAreas,
    selectAllBusinessAreas,
    moveStackOrderUp,
    moveStackOrderDown,
    resetStackOrder,
    updateStackOrderColor,
    saveError,
  }
}
